import React, { useState, useEffect, useMemo } from 'react';
import {
  BookOpen, Loader2, Plus, Search, FileText, Trash2, Download,
  Tag, Save, X, Pencil,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KnowledgeTab = ({ password }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [activeTag, setActiveTag] = useState('');

  // form
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [tags, setTags] = useState('');
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  // edit
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editTags, setEditTags] = useState('');

  const headers = useMemo(() => ({ 'x-admin-password': password }), [password]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (activeTag) params.set('tag', activeTag);
      const res = await fetch(`${API}/admin/knowledge/items?${params}`, { headers });
      const data = await res.json();
      setItems(data.items || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); /* eslint-disable-next-line */ }, [activeTag]);

  const handleCreate = async (e) => {
    e?.preventDefault?.();
    if (!title.trim()) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('title', title.trim());
      fd.append('notes', notes);
      fd.append('tags', tags);
      if (file) fd.append('file', file);
      const res = await fetch(`${API}/admin/knowledge/items`, {
        method: 'POST', headers, body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Speichern fehlgeschlagen');
      }
      setTitle(''); setNotes(''); setTags(''); setFile(null);
      setShowForm(false);
      fetchItems();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditTitle(item.title);
    setEditNotes(item.notes || '');
    setEditTags((item.tags || []).join(', '));
  };

  const saveEdit = async (id) => {
    try {
      const tagList = editTags.split(',').map((t) => t.trim()).filter(Boolean);
      await fetch(`${API}/admin/knowledge/items/${id}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: editTitle.trim(), notes: editNotes, tags: tagList }),
      });
      setEditingId(null);
      fetchItems();
    } catch (err) {
      console.error(err);
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Eintrag wirklich löschen?')) return;
    try {
      await fetch(`${API}/admin/knowledge/items/${id}`, { method: 'DELETE', headers });
      fetchItems();
    } catch (err) { console.error(err); }
  };

  const downloadFile = async (item) => {
    try {
      const res = await fetch(`${API}/admin/knowledge/items/${item.id}/file`, { headers });
      if (!res.ok) throw new Error('Download fehlgeschlagen');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = item.filename || 'datei';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { alert(err.message); }
  };

  const allTags = useMemo(() => {
    const s = new Set();
    items.forEach((i) => (i.tags || []).forEach((t) => s.add(t)));
    return Array.from(s).sort();
  }, [items]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const term = q.trim().toLowerCase();
    return items.filter((i) =>
      (i.title || '').toLowerCase().includes(term) ||
      (i.notes || '').toLowerCase().includes(term) ||
      (i.filename || '').toLowerCase().includes(term)
    );
  }, [items, q]);

  const fmtSize = (b) => {
    if (!b) return '';
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-6" data-testid="knowledge-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <BookOpen size={18} className="text-slate-700" />
            Wissensdatenbank ({items.length})
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Eigene Vorlagen, Studien, Snippets, PDFs — alles, was du beim Beraten griffbereit haben willst.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowForm(!showForm)}
          data-testid="knowledge-add-btn"
        >
          <Plus size={14} className="mr-2" /> Neuer Eintrag
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Suchen (Titel, Notizen, Dateiname) …"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-8"
            data-testid="knowledge-search"
          />
        </div>
        {allTags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => setActiveTag('')}
              className={`text-xs px-2 py-1 rounded-full transition-colors ${
                activeTag === '' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              Alle
            </button>
            {allTags.map((t) => (
              <button
                key={t}
                onClick={() => setActiveTag(t === activeTag ? '' : t)}
                className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full transition-colors ${
                  activeTag === t ? 'bg-orange-500 text-white' : 'bg-orange-50 text-orange-700 hover:bg-orange-100'
                }`}
              >
                <Tag size={10} />
                {t}
              </button>
            ))}
          </div>
        )}
      </div>

      {showForm && (
        <form
          onSubmit={handleCreate}
          className="bg-white rounded-lg border border-slate-200 p-5 shadow-sm space-y-3"
          data-testid="knowledge-form"
        >
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-900">Neuer Wissens-Eintrag</h3>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="text-slate-400 hover:text-slate-700"
            >
              <X size={18} />
            </button>
          </div>
          <Input
            placeholder="Titel *"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            data-testid="knowledge-title-input"
          />
          <Textarea
            placeholder="Notizen / Beschreibung (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="min-h-[80px]"
          />
          <Input
            placeholder="Tags (Komma-getrennt, z. B. krankenhaus, vorlage, einwand)"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
          />
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <label className="text-xs font-medium text-slate-600">Datei (optional, max 8 MB):</label>
            <input
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="text-xs"
              data-testid="knowledge-file-input"
            />
            {file && (
              <span className="text-xs text-slate-500">
                {file.name} · {fmtSize(file.size)}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button type="submit" disabled={saving || !title.trim()} className="bg-slate-800 hover:bg-slate-700">
              {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />}
              Speichern
            </Button>
            <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
              Abbrechen
            </Button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="animate-spin text-slate-400" size={22} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
          <BookOpen size={32} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">
            {items.length === 0 ? 'Noch keine Einträge.' : 'Keine Treffer für deine Suche.'}
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {filtered.map((it) => {
            const editing = editingId === it.id;
            return (
              <li
                key={it.id}
                className="bg-white border border-slate-200 rounded-md p-4 hover:border-slate-300 transition-colors"
                data-testid={`knowledge-item-${it.id}`}
              >
                {editing ? (
                  <div className="space-y-2">
                    <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} />
                    <Textarea value={editNotes} onChange={(e) => setEditNotes(e.target.value)} className="min-h-[70px]" />
                    <Input placeholder="Tags (Komma-getrennt)" value={editTags} onChange={(e) => setEditTags(e.target.value)} />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => saveEdit(it.id)} className="bg-slate-800">
                        <Save size={13} className="mr-1.5" /> Speichern
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>
                        Abbrechen
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-semibold text-slate-900">{it.title}</h4>
                          {(it.tags || []).map((t) => (
                            <span key={t} className="inline-flex items-center gap-1 text-[11px] bg-orange-50 text-orange-700 px-1.5 py-0.5 rounded">
                              <Tag size={9} /> {t}
                            </span>
                          ))}
                        </div>
                        {it.notes && (
                          <p className="text-sm text-slate-600 mt-1.5 whitespace-pre-wrap">
                            {it.notes}
                          </p>
                        )}
                        {it.filename && (
                          <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-slate-500 bg-slate-50 px-2 py-1 rounded">
                            <FileText size={11} />
                            {it.filename}
                            <span className="text-slate-400">· {fmtSize(it.size_bytes)}</span>
                          </div>
                        )}
                        <div className="text-[10px] text-slate-400 mt-2">
                          {new Date(it.created_at).toLocaleString('de-DE')}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {it.filename && (
                          <button
                            onClick={() => downloadFile(it)}
                            className="p-1.5 hover:bg-slate-100 rounded transition-colors"
                            title="Datei herunterladen"
                            data-testid={`knowledge-download-${it.id}`}
                          >
                            <Download size={14} className="text-slate-700" />
                          </button>
                        )}
                        <button
                          onClick={() => startEdit(it)}
                          className="p-1.5 hover:bg-slate-100 rounded transition-colors"
                          title="Bearbeiten"
                        >
                          <Pencil size={13} className="text-slate-500" />
                        </button>
                        <button
                          onClick={() => deleteItem(it.id)}
                          className="p-1.5 hover:bg-red-50 rounded transition-colors"
                          title="Löschen"
                        >
                          <Trash2 size={13} className="text-red-500" />
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default KnowledgeTab;
